import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../lib/axios'
import { PageHeader, Card, Spinner, EmptyState, StatusBadge } from '../../components/ui'
import ShareModal          from '../../components/sharing/ShareModal'
import VersionHistoryPanel from '../../components/sharing/VersionHistoryPanel'
import DiffStatusBanner    from '../../components/diff/DiffStatusBanner'
import TestPlanRulePanel   from '../../components/testplan-rule/TestPlanRulePanel'
import toast from 'react-hot-toast'

function fmt(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
}

export default function TestPlans() {
  const navigate = useNavigate()
  const [plans, setPlans]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [shareDoc, setShareDoc]     = useState(null)
  const [historyDoc, setHistoryDoc] = useState(null)
  const [ruleRepoId, setRuleRepoId] = useState(null)  // show rule panel for a repo
  const [repos, setRepos]           = useState([])

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      api.get('/test-plans'),
      api.get('/repositories'),
    ]).then(([plansRes, reposRes]) => {
      const pd = plansRes.data?.data ?? plansRes.data
      setPlans(Array.isArray(pd) ? pd : [])
      const rd = reposRes.data?.data ?? reposRes.data
      setRepos(Array.isArray(rd) ? rd : [])
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const generateRolePdf = async (plan) => {
    const tid = toast.loading('Generating role test plan PDF…')
    try {
      const res = await api.post(`/test-plans/${plan.id}/generate-pdf/role-test-plan`)
      toast.success('PDF ready!', { id: tid })
      window.open(res.data.download_url, '_blank')
    } catch { toast.error('Generation failed.', { id: tid }) }
  }

  const startExecution = async (plan) => {
    const tid = toast.loading('Creating execution…')
    try {
      const res = await api.post('/executions', {
        test_plan_id: plan.id,
        title: `Execution — ${plan.title}`,
        environment: 'staging',
      })
      toast.success('Execution created', { id: tid })
      navigate(`/dashboard/test-plans/${plan.id}/execute/${res.data.id}`)
    } catch { toast.error('Failed to create execution.', { id: tid }) }
  }

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg,#0d0521 0%,#07020f 100%)', minHeight: '100%' }}>
      <PageHeader title="Test Plans" subtitle={`${plans.length} generated`} />
      <div className="px-8 space-y-8">

        {/* ── Test Plan Generation Rules section ── */}
        {repos.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-white/40 uppercase tracking-widest">Generation Rules</p>
              <p className="text-xs text-white/25">Test plans are never auto-generated — configure per repo</p>
            </div>
            <div className="flex gap-2 flex-wrap mb-4">
              {repos.map(repo => (
                <button key={repo.id} onClick={() => setRuleRepoId(ruleRepoId === repo.id ? null : repo.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                    ruleRepoId === repo.id
                      ? 'bg-amber-500/15 border-amber-500/35 text-amber-300'
                      : 'bg-white/4 border-white/10 text-white/50 hover:border-white/20'}`}>
                  <span>{repo.provider === 'github' ? '⌥' : repo.provider === 'gitlab' ? '🦊' : '🪣'}</span>
                  {repo.name ?? repo.full_name.split('/')[1]}
                  <span className="text-[9px] opacity-60">⚙</span>
                </button>
              ))}
            </div>
            {ruleRepoId && (
              <TestPlanRulePanel
                repositoryId={ruleRepoId}
                defaultBranch={repos.find(r => r.id === ruleRepoId)?.default_branch ?? 'main'}
                repoFullName={repos.find(r => r.id === ruleRepoId)?.full_name ?? ''}
              />
            )}
          </div>
        )}

        {/* ── Test Plans list ── */}
        <div>
          <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3">Generated Test Plans</p>
          {loading ? (
            <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
          ) : plans.length === 0 ? (
            <Card><EmptyState icon="🧪" title="No test plans yet"
              description="Select a repository above, configure a generation rule, and click 'Generate Test Plan'." /></Card>
          ) : (
            <div className="space-y-3">
              {plans.map(plan => (
                <div key={plan.id} className="glass-card overflow-hidden">

                  {plan.status === 'complete' && plan.last_generated_commit_sha && (
                    <DiffStatusBanner type="test-plan" id={plan.id} onRegenQueued={load} />
                  )}

                  <div className="flex items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-lg">🧪</span>
                        <p className="text-sm font-semibold text-white truncate">{plan.title}</p>
                        {plan.is_public && (
                          <span className="text-[9px] font-bold uppercase text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full border border-emerald-500/20">SHARED</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <StatusBadge status={plan.status} />
                        {plan.coverage_type && (
                          <span className="text-xs capitalize text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded-full">{plan.coverage_type}</span>
                        )}
                        <span className="text-xs text-white/35">{plan.repo_name}</span>
                        <span className="text-xs text-white/25">v{plan.version ?? 1}</span>
                        {plan.test_case_count && <span className="text-xs text-white/25">{plan.test_case_count} cases</span>}
                        <span className="text-xs text-white/25">{fmt(plan.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      {plan.status === 'complete' && (
                        <>
                          <button onClick={() => startExecution(plan)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 text-purple-200 text-xs font-semibold hover:from-purple-500/30 hover:to-blue-500/30 transition-all">
                            ▶ Run Execution
                          </button>
                          <button onClick={() => generateRolePdf(plan)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-medium hover:bg-indigo-500/20 transition-all">
                            📋 Role PDF
                          </button>
                          <button onClick={() => setHistoryDoc(historyDoc?.id === plan.id ? null : plan)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs font-medium hover:bg-white/10 transition-all">
                            🕐 History
                          </button>
                          <button onClick={() => setShareDoc(plan)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                              plan.is_public
                                ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                                : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}>
                            🔗 Share
                          </button>
                          {plan.public_url && (
                            <a href={plan.public_url} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-medium hover:bg-purple-500/20 transition-all">
                              ↓ Download
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {historyDoc?.id === plan.id && (
                    <div className="border-t border-white/6 px-5 py-4">
                      <VersionHistoryPanel type="test-plan" id={plan.id} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {shareDoc && (
        <ShareModal type="test-plan" id={shareDoc.id} title={shareDoc.title}
          onClose={() => { setShareDoc(null); load() }} />
      )}
    </div>
  )
}
